import { NET } from './constants.js';
import { lerpPose } from './history.js';
export class Timeline {
    capacity;
    states = [];
    constructor(capacity = NET.CLIENT_RING_TICKS) {
        this.capacity = capacity;
    }
    get size() {
        return this.states.length;
    }
    latest() {
        return this.states[this.states.length - 1] ?? null;
    }
    byTick(tick) {
        // Ticks are contiguous once the ring is warm; fall back to a scan after a resync.
        const last = this.states[this.states.length - 1];
        if (!last)
            return null;
        const guess = this.states.length - 1 - (last.tick - tick);
        const hit = this.states[guess];
        if (hit && hit.tick === tick)
            return hit;
        return this.states.find((s) => s.tick === tick) ?? null;
    }
    /** Adds an authoritative state; a repeated tick replaces the earlier copy. */
    push(state) {
        const last = this.states[this.states.length - 1];
        if (last && state.tick < last.tick) {
            // Older than what we hold (a full snapshot after a resync jumped back): start over.
            this.states.length = 0;
        }
        else if (last && state.tick === last.tick) {
            this.states[this.states.length - 1] = state;
            return;
        }
        this.states.push(state);
        while (this.states.length > this.capacity)
            this.states.shift();
    }
    clear() {
        this.states.length = 0;
    }
    /** The two states around a fractional tick (clamped to the ring, extrapolating a little). */
    sample(tick) {
        const n = this.states.length;
        if (n === 0)
            return null;
        const first = this.states[0];
        const last = this.states[n - 1];
        if (n === 1 || tick <= first.tick)
            return { a: first, b: first, alpha: 0 };
        if (tick >= last.tick) {
            const prev = this.states[n - 2];
            const span = last.tick - prev.tick;
            const beyond = span > 0 ? (tick - last.tick) / span : 0;
            return { a: prev, b: last, alpha: 1 + Math.min(beyond, NET.MAX_EXTRAPOLATION_TICKS) };
        }
        let lo = 0;
        let hi = n - 1;
        while (hi - lo > 1) {
            const mid = (lo + hi) >> 1;
            if (this.states[mid].tick <= tick)
                lo = mid;
            else
                hi = mid;
        }
        const a = this.states[lo];
        const b = this.states[hi];
        const span = b.tick - a.tick;
        return { a, b, alpha: span > 0 ? (tick - a.tick) / span : 0 };
    }
    /** The drawn pose of `slot` at a fractional tick (null before the first state). */
    pose(slot, tick) {
        const s = this.sample(tick);
        if (!s)
            return null;
        const a = s.a.mechs[slot].body;
        const b = s.b.mechs[slot].body;
        if (s.alpha <= 1)
            return lerpPose(a, b, s.alpha);
        // Extrapolate along the last velocity rather than blending past the newest state.
        const dt = (s.alpha - 1) * (s.b.tick - s.a.tick);
        return {
            position: {
                x: b.position.x + (b.position.x - a.position.x) * dt,
                y: b.position.y + (b.position.y - a.position.y) * dt,
                z: b.position.z + (b.position.z - a.position.z) * dt,
            },
            yaw: b.yaw,
            aimYaw: b.aimYaw,
            aimPitch: b.aimPitch,
        };
    }
    /** The state the drawn opponent belongs to (its HP, effects, cloak) at a fractional tick. */
    stateAt(tick) {
        const s = this.sample(tick);
        return s ? (s.alpha >= 0.5 ? s.b : s.a) : null;
    }
    /** Projectiles of `slot` blended between the bracketing states (new ones at their first pose). */
    projectiles(slot, tick) {
        const s = this.sample(tick);
        if (!s)
            return [];
        const k = Math.max(0, Math.min(1, s.alpha));
        const before = new Map(s.a.projectiles.map((p) => [p.id, p]));
        const out = [];
        for (const p of s.b.projectiles) {
            if (p.slot !== slot)
                continue;
            const q = before.get(p.id);
            out.push({
                id: p.id,
                slot: p.slot,
                weapon: p.weapon,
                position: q
                    ? {
                        x: q.position.x + (p.position.x - q.position.x) * k,
                        y: q.position.y + (p.position.y - q.position.y) * k,
                        z: q.position.z + (p.position.z - q.position.z) * k,
                    }
                    : { ...p.position },
            });
        }
        return out;
    }
}
//# sourceMappingURL=timeline.js.map